import {
  ApprovalActorType,
  ApprovalHeadline,
  ApprovalPrinterStatus,
  ApprovalState,
  AssetKind,
  BookCancelBlockedReason,
  BookCancelReasonCategory,
  BookRefundFlag,
  FlipBookStatus,
  ItpRefusalCode,
  PrintFileRefusalCode,
  TicketAction,
  TicketFileKind,
  TicketFileUnavailableReason,
  TicketItpStatus,
} from './types';

/**
 * Centralized user-facing copy for the print-shop (Mail-on-Demand) admin
 * pages.
 *
 * This repo has no dedicated i18n framework yet, so — per the project rule
 * that feature text must not be hardcoded inline — every label/message the
 * pages render is sourced from this single module and interpolated with
 * `formatMessage`. When a real i18n library is adopted repo-wide, this object
 * is the natural seed for the default-locale catalog.
 */

/**
 * The refusal texts the print-file routes and the ITP upload share (same DS
 * codes), written once so both cards say the same thing.
 */
const SHARED_FILE_REFUSALS = {
  PRINT_FILE_MISSING: 'No file was received. Choose the PDF again.',
  PRINT_FILE_TOO_LARGE: 'The file is over 100 MB. Choose a smaller PDF.',
  PRINT_FILE_NOT_PDF: 'That file is not a PDF.',
  ABO_OPERATOR_UNVERIFIED:
    'Your sign-in could not be verified. Sign in to ABO again, then retry.',
  ABO_OPERATOR_UNAVAILABLE:
    'This change is not available right now: operator sign-in checks are not set up. Try again later.',
} as const;

const NO_BUCKET_REFUSAL =
  'This order has no generated cover or manuscript to store the file next to.';

export const printShopStrings = {
  sidebar: {
    groupLabel: 'Print Shop',
    // One entry per queue (abo-print-shop-menu-split): the former "Ticket
    // Queue" page and its "Mail on Demand | Books" tabs became two pages.
    modQueue: 'Mail on Demand',
    booksQueue: 'Books',
    pricingConfig: 'Pricing Config',
    // New in task_07: third print-shop nav item, linking to the Batches list
    // (`/print-shop/batches`). Badge count next to it comes from
    // `fetchBatchCounts` (task_02), not from copy here.
    batches: 'Batches',
  },
  /** Book-order facts that have no mail-on-demand equivalent. */
  archive: {
    banner:
      'Imported record — this order was placed before the back office handled book orders.',
    bannerHint:
      'It is here so it can be looked up. There is nothing to action, and no fulfilment history was recoverable for it.',
    badge: 'Imported',
    cancelledBadge: 'Cancelled',
    cancelledBanner:
      'This order was cancelled. The record is here so it can be looked up; there is nothing to do.',
    formerMember: 'Former member',
    formerMemberHint:
      'This order was placed by someone who is no longer a member, so there is no profile to open.',
    lookupTitle: 'Find a past order',
    lookupHint:
      'Search by the order number a member quotes, or by their email. This searches every order, including imported records that are not in the queue.',
    lookupPlaceholder: 'Order number or email',
    lookupButton: 'Find',
    lookupSearching: 'Searching…',
    lookupEmpty: 'No order matches that. Check the number or the email address.',
    lookupResults: '{count} matching {count, plural, one {order} other {orders}}',
    lookupFailed: 'The search could not be completed. Try again.',
  },
  book: {
    orderNumber: 'Order number',
    copies: 'Copies',
    copiesUnit: (n) => (n === 1 ? '1 copy' : `${n} copies`),
    shipTo: 'Ship to',
    shipToMissing: 'No shipping address on this order',
    shipToMissingHint:
      'This order arrived without an address, so it cannot be shipped until one is found. It was never guessed.',
    // Ship-to edit in the order facts (abo-print-files-upload).
    editShipTo: 'Edit',
    editShipToAriaLabel: 'Edit the ship-to address',
    shipToFields: {
      address1: 'Address line 1',
      address2: 'Address line 2',
      city: 'City',
      state: 'State',
      zip: 'ZIP',
      country: 'Country',
    },
    shipToRegenerateInvoice: 'Regenerate the invoice with the new address',
    // Under the checkbox when the current invoice is an operator upload; the
    // box then starts unchecked.
    shipToRegenerateReplacesUpload:
      'Your uploaded invoice will be replaced if checked.',
    shipToCancel: 'Cancel',
    shipToSave: 'Save address',
    shipToSaving: 'Saving…',
    shipToSaved: 'Address saved',
    shipToSavedInvoiceRegenerated: 'Address saved; invoice regenerated',
    shipToSavedInvoiceFailed:
      'Address saved; the invoice could not be regenerated — use Regenerate on the Invoice row',
    shipToInvalid:
      'Fill in every address line except line 2 (200 characters at most each).',
    shipToSaveFailed: 'Could not save the address. Try again.',
    printFilesTitle: 'Print files',
    fileLabels: {
      cover: 'Cover',
      manuscript: 'Manuscript',
      invoice: 'Invoice',
    },
    download: 'Download',
    downloading: 'Preparing…',
    // Typed against the reason union so a new reason cannot ship unlabelled:
    // `no-longer-stored` once did, and rendered as a blank cell.
    unavailable: {
      'not-rendered-yet': 'Not ready yet — still being rendered',
      'not-generated': 'Never produced for this order',
      'no-longer-stored': 'No longer stored — this imported record kept no file',
      'lookup-failed': 'Could not check right now — try again',
    } satisfies Record<TicketFileUnavailableReason, string>,
    // The flip book and lead-capture site (order-flipbook-links). Public pages,
    // opened in a new tab — not print files.
    linkLabels: {
      flipBook: 'Flip book',
      leadSite: 'Lead capture site',
    },
    openLink: 'Open',
    flipBookPreviewHint: 'Preview — the book is not published yet',
    linkUnavailable: {
      flipBook: 'No flip book for this book yet',
      leadSite: 'No lead capture site',
    },
    linkLookupFailed: 'Could not check right now — try again',
    // Replace the member's flip book PDF (abo-print-files-flipbook-sync).
    flipBookReplacedBy: 'Replaced by {email} · {date}',
    // `unknown` (afy-flipping did not answer) shows no state at all.
    flipBookStatuses: {
      processing: 'Processing…',
      ready: 'Ready',
      failed: 'Processing failed',
    } satisfies Record<Exclude<FlipBookStatus, 'unknown'>, string>,
    flipBookOutOfDate: 'Out of date',
    flipBookOutOfDateHint: 'May show the previous cover or manuscript',
    // Under the Cover / Manuscript row right after its replace; {kind} is a
    // `fileNouns` entry.
    flipBookReminder: 'The flip book still shows the previous {kind}. Update it too?',
    flipBookReminderReplace: 'Replace flip book PDF',
    flipBookReminderDismiss: 'Dismiss',
    flipBookFootnote:
      "The flip book is the member's published book (book.live and lead site). Replace takes the whole-book PDF, up to 100 MB; it can take a few minutes, and up to a day to show everywhere. A new publish from the book builder replaces it.",
    flipBookReplaced:
      'Flip book PDF replaced. It can take a few minutes to update.',
    flipBookReplaceFailed: 'The flip book could not be replaced. Try again.',
    downloadFailed: 'Could not produce a download link. Try again.',
    regenerateInvoice: 'Generate invoice',
    regeneratingInvoice: 'Generating…',
    regenerateInvoiceFailed:
      'The invoice service did not return a document. Try again in a moment.',
    // The small link on the Invoice row when an invoice exists (editable
    // tickets only): asks DS for a new generated invoice.
    regenerateExistingInvoice: 'Regenerate',
    // The inline confirmation of a regenerate when the current invoice is an
    // operator upload: generating makes the new generated invoice current.
    regenerateReplacesUpload: 'This replaces the uploaded invoice.',
    regenerateConfirmButton: 'Regenerate',
    regenerateCancelButton: 'Cancel',
    regenerateInvoiceDone: 'Invoice regenerated.',
    // Replace / Restore a print file (abo-print-files-upload, option A).
    replaceFile: 'Replace',
    uploadingFile: 'Uploading… {percent}%',
    // The file is all sent; DS is still storing it (print file) or handing it
    // to the flip book service (flip book), so the action stays disabled.
    savingFile: 'Saving…',
    updatingFlipBook: 'Updating the flip book…',
    uploadedBy: 'Uploaded by {email} · {date}',
    restoreGenerated: 'Restore generated',
    // Lower-case kind nouns for sentences ("Restore the generated cover?").
    fileNouns: {
      cover: 'cover',
      manuscript: 'manuscript',
      invoice: 'invoice',
    } satisfies Record<TicketFileKind, string>,
    restoreConfirm: 'Restore the generated {kind}? Your upload stays stored.',
    restoreConfirmButton: 'Restore',
    restoreCancelButton: 'Cancel',
    restoring: 'Restoring…',
    replaceFootnote:
      'Replace takes a PDF up to 100 MB. The previous file is kept.',
    fileReplaced: '{kind} replaced.',
    fileRestored: 'Generated {kind} restored.',
    // Checked in the browser before anything is sent.
    fileNotPdf: 'Choose a PDF file.',
    fileTooLarge: 'The file is over 100 MB. Choose a smaller PDF.',
    uploadFailed: 'The upload failed. Try again.',
    signInAgain: 'Sign in again',
    restoreFailed: 'Could not restore the generated file. Try again.',
    // ABO's own text per DS refusal code (`print-file-refusal.ts`).
    fileRefusals: {
      PRINT_FILE_ORDER_NOT_EDITABLE:
        'This order can no longer be changed (shipped, cancelled or imported).',
      PRINT_FILE_NOT_BOOK: 'Only book orders have print files to replace.',
      ...SHARED_FILE_REFUSALS,
      PRINT_FILE_NO_BUCKET: NO_BUCKET_REFUSAL,
      PRINT_FILE_NOTHING_TO_RESTORE:
        'There is no generated file to restore — it may already be current.',
      FLIPBOOK_NOT_FOUND: 'The member has no published flip book for this book.',
      FLIPBOOK_NOT_READY:
        'The flip book is still being processed. Try again in a few minutes.',
      FLIPBOOK_REPLACE_FAILED:
        'The flip book service could not replace the PDF. Try again in a moment.',
      FLIPBOOK_UNAVAILABLE:
        'Flip book replace is not available right now: it is not set up. Try again later.',
      FLIPBOOK_REPLACE_TIMEOUT:
        'The flip book service did not answer in time. The flip book may still be updating: reload the ticket and check the Flip book row before you replace it again.',
    } satisfies Record<PrintFileRefusalCode, string>,
    // A book order ships N copies to ONE address. It has no audience, so the
    // mailing wording ("recipients", "mailing list") does not apply to it.
    shipmentTitle: 'Shipment',
    shipmentSummary: (copies) =>
      `${copies === 1 ? '1 copy' : `${copies} copies`} to one address`,
    creditsUnknown: 'Not recorded',
  },
  queue: {
    pageTitle: 'Print Shop Queue',
    /**
     * Books vs mailing. Two distinct jobs — different machines, different
     * shipping shape — so each has its own page and menu entry rather than a
     * shared queue behind a filter (abo-print-shop-menu-split ADR-001).
     */
    titles: {
      mod: 'Mail on Demand',
      book: 'Books',
    },
    // Only Books has one: it was the former Books tab's hover hint.
    subtitles: {
      book: 'Printed book orders placed by members in the book builder.',
    } as Partial<Record<'mod' | 'book', string>>,
    refreshButton: 'Refresh',
    tabAll: 'All',
    searchPlaceholder: 'Search member email, name, or order id…',
    assetKindFilterAll: 'All asset kinds',
    columns: {
      order: 'Order',
      member: 'Member',
      asset: 'Asset',
      quantity: 'Qty',
      paidAt: 'Paid',
      ageDays: 'Age',
      status: 'Status',
      trackingRef: 'Tracking',
    },
    noTracking: '—',
    empty: 'No tickets found for this filter.',
    loadError: 'Could not load the print-shop queue. Please try again.',
    copyOrderId: 'Copy order id',
    copyOrderNumber: 'Copy order number',
    copied: 'Copied',
    perPage: '{limit} per page',
    pageIndicator: 'Page {currentPage} of {lastPage}',
    prev: 'Prev',
    next: 'Next',
    slaNote:
      '⚠ Red bar = ticket in Paid (or Waiting for ITP) with no action for {days} or more days (SLA highlight).',
    slaTooltip: 'Paid {days}+ days ago with no operator action',
    // A Waiting for ITP row gets the same bar (itp-mvp).
    slaTooltipItp: 'Waiting for its ITP for {days}+ days',
    // Asset-type chip + two-machine (cover) indicator on the Asset cell (task 07),
    // so an operator can route print files without opening every ticket.
    assetTypeBook: 'Book',
    assetTypeExport: 'TB export',
    coverBadge: '+ cover',
    coverHint:
      'This book has a separate cover — cover + manuscript print on two machines.',
    // The Instant Trust Package chip on the Asset cell (itp-mvp).
    itpBadge: 'ITP',
    itpReadyBadge: '✓ ITP ready',
    itpHint:
      'This order includes an Instant Trust Package. It ships in the same box as the books.',
    itpReadyHint: 'The Instant Trust Package of this order is marked ready.',
  },
  detail: {
    pageTitle: 'Ticket {orderId}',
    backToQueue: 'Back to queue',
    backTo: 'Back to {queue}',
    loading: 'Loading ticket…',
    notFound:
      'Ticket not found. Orders only get a print ticket after payment is confirmed.',
    loadError: 'Could not load this ticket. Please try again.',
    copyFullId: 'Copy id',
    copiedId: 'Copied!',
    // Horizontal progress stepper in the hero (task 42). "Submitted" is always
    // complete because a ticket only exists after the order was placed + paid.
    stepper: {
      submitted: 'Submitted',
      // Only on an order that includes an Instant Trust Package (itp-mvp).
      itp_pending: 'Waiting for ITP',
      paid: 'Paid',
      accepted: 'Accepted',
      in_production: 'In production',
      shipped: 'Shipped',
      // The end of an operator-cancelled ticket's stepper, after the status
      // it was cancelled from.
      cancelled: 'Cancelled',
    },
    member: {
      label: 'Member',
      unknown: 'Unknown member',
    },
    asset: {
      sectionTitle: 'Asset',
      kindLabel: 'Type',
      // Header of the print-files card, e.g. "Print files — Book" (task 42).
      filesSectionTitle: 'Print files',
      downloadPdf: 'Download asset PDF',
      // Books print as two files on different machines (task 41).
      coverTitle: 'Book cover',
      manuscriptTitle: 'Manuscript',
      download: 'Download',
      // Open the PDF inline in a new browser tab (view, not save) (task 51).
      preview: 'Preview',
      // Short file-type chips on the print-file cards (task 42).
      fileBadgeCover: 'COVER',
      fileBadgeManuscript: 'BOOK',
      fileBadgePdf: 'PDF',
      downloadCover: 'Download cover PDF',
      downloadManuscript: 'Download manuscript PDF',
      downloadBookPdf: 'Download book PDF',
      // Accessible labels for the per-file Preview action (task 51).
      previewCover: 'Preview cover PDF',
      previewManuscript: 'Preview manuscript PDF',
      previewBookPdf: 'Preview book PDF',
      previewPdf: 'Preview PDF',
      twoFileNote: 'Cover and manuscript print on separate machines.',
      // Cover-letter example preview in the print-files section (book add-on).
      // The letter is personalized per recipient, so this is only ever a
      // representative example pulled from the first recipient whose letter has
      // rendered — with a placeholder until one is ready.
      coverLetterExampleTitle: 'Cover letter',
      coverLetterExampleNote: 'Personalized per recipient',
      coverLetterExamplePlaceholder:
        'Example available once the first letter renders',
      coverLetterExamplePreviewAria: 'Preview an example cover letter',
      // Human labels for the cover-letter template a book add-on uses.
      coverLetterTemplateLabels: {
        classic: 'Classic',
        modern: 'Modern',
      } as Record<string, string>,
    },
    recipients: {
      sectionTitle: 'Recipients',
      // The print shop only ever mails the selected set, so the card shows the
      // mailing list alone (the excluded/"No address" view was removed in
      // task 56). The summary is the mailable count + a plain "Mailing list"
      // label — no "excluded" count an operator can't act on.
      summary: '{mailable} recipients · Mailing list',
      // Per-row badge (the recipient will receive mail).
      mailingBadge: 'Mailing',
      tableEmpty: 'No recipients to show here.',
      loadError: 'Could not load recipients. Please try again.',
      correctedBadge: '✎ member-corrected',
      correctedOriginal: 'Original (from Marketing Hub): {address}',
      // Page-size selector on the recipients table (task 47): mirrors the
      // member-side 10/25/50/100 control wired to the existing `limit` param.
      perPageLabel: 'Per page',
      pageSizeAriaLabel: 'Recipients per page',
      downloadCsv: 'Download recipients CSV',
      downloadCsvError:
        'Could not download the recipients CSV. Please try again.',
      // Column headers (task 06). The cover-letter header only shows when the
      // order includes a personalized letter.
      columnName: 'Name',
      columnAddress: 'Mailing address',
      columnStatus: 'Status',
      // Per-recipient personalized cover letter (the book add-on). The letter
      // PDF is rendered per lead and pulled here for printing alongside the book.
      coverLetterColumn: 'Cover letter',
      coverLetterPreview: 'Preview',
      coverLetterDownload: 'PDF',
      coverLetterPreviewAria: 'Preview the cover letter for {name}',
      coverLetterDownloadAria: 'Download the cover-letter PDF for {name}',
      // Render-status states (task 03): a not-yet-rendered or failed letter must
      // never read as "no letter".
      // A still-rendering (pending / not-yet-started) letter shows an active
      // spinner so an operator sees the job is in flight, never a bare em-dash.
      coverLetterGenerating: 'Generating…',
      // Still auto-retrying (not yet terminal) — reassure the operator the job
      // is in flight rather than showing a scary "failed" mid-retry.
      coverLetterRetrying: 'Retrying…',
      coverLetterRetryingWithAttempt: 'Retrying… (attempt {attempts})',
      coverLetterFailed: 'Render failed',
      // Terminal failure with a friendly reason from DS; the raw error shows on
      // hover (title). Falls back to the bare `coverLetterFailed` when DS sends
      // no reason (older deploy).
      coverLetterFailedReason: 'Render failed — {reason}',
      coverLetterFailedHint:
        'This personalized letter failed to render. Use Retry to try again.',
      // Manual retry for a terminally-failed render (mod-cover-letter-manual-retry):
      // only shown once the auto-retry loop has given up (`coverLetterRetrying`
      // is false), so it never overlaps the "Retrying…" badge above.
      coverLetterRetryButton: 'Retry',
      coverLetterRetryButtonPending: 'Retrying…',
      coverLetterRetryTriggered: 'Cover-letter render re-triggered.',
      coverLetterRetryError:
        'Could not retry the cover-letter render. Please try again.',
      // "Download all letters" bundle (task 04): one zip of every rendered letter.
      downloadAllLetters: 'Download all letters',
      downloadAllLettersEmpty: 'No cover letters are ready to download yet.',
      downloadAllLettersSuccess: 'Downloaded {count} cover letters.',
      downloadAllLettersPartial:
        'Downloaded {count} of {total} cover letters — {pending} are still rendering or failed.',
      downloadAllLettersError:
        'Could not download the cover letters. Please try again.',
    },
    payment: {
      sectionTitle: 'Payment',
      paidAt: 'Paid at',
      creditsCharged: 'Credits charged',
    },
    shipping: {
      // A MOD order mails one asset to N recipients via a bulk/batch method,
      // so this single value is a batch/shipment reference (manifest/job id),
      // NOT a per-recipient parcel tracking number — labelled accordingly
      // (task 55). The DB field stays `shipping.trackingRef` (internal).
      trackingRef: 'Shipment reference',
      // Sets the expectation that recipients aren't individually tracked, so
      // the single reference never reads as per-parcel carrier tracking.
      notIndividuallyTracked: "Individual recipients aren't tracked separately.",
      notShipped: 'Not shipped yet',
      // Inline "Edit reference" correction control (task 53). Operators fix a
      // fat-fingered reference after it has been set; admin-scoped (PII).
      editTracking: 'Edit reference',
      editTrackingAriaLabel: 'Edit the shipment reference',
      trackingInputLabel: 'Shipment reference',
      trackingPlaceholder: 'e.g. 1Z999AA10123456784',
      saveTracking: 'Save',
      cancelTracking: 'Cancel',
      trackingRequired: 'Enter a shipment reference before saving.',
      trackingUpdated: 'Shipment reference updated.',
      trackingUpdateError:
        'Could not update the shipment reference. Please try again.',
    },
    history: {
      sectionTitle: 'History',
      empty: 'No status changes recorded yet.',
      transition: '{from} → {to}',
      // An INGESTED order was CREATED in its status rather than transitioned
      // into it, so its event carries no `from`. Without this the panel printed
      // the literal "undefined → Paid".
      created: 'Created · {to}',
      actor: 'by {actor}',
      // The timeline collapses to the most recent few events with a toggle to
      // reveal the rest (task 57); the full audit set is preserved, just
      // hidden. Accessible: the toggle is a real button with state.
      showAll: 'Show all ({count})',
      showLess: 'Show less',
      // A status CORRECTION (task 60) is a real from→to change, but reads as a
      // correction rather than a normal forward transition.
      corrected: 'Status corrected: {from} → {to}',
      // An operator cancel of a book order; older records add their
      // reason category.
      operatorCancelled: 'Cancelled by support',
      operatorCancelledWithCategory: 'Cancelled by support · {category}',
      // Same-status entries carry a `meta.type` so the row reads sensibly
      // instead of a bare "shipped → shipped" (task 57; e.g. a `tracking_updated`
      // edit from task 53). Falls back to a generic "updated" label.
      metaLabels: {
        tracking_updated: 'Shipment reference updated',
        status_corrected: 'Status corrected',
        // Same-status approval events (book-order-approval techspec §4, §8).
        approval_revised: 'Member updated the order — new approval e-mail',
        approval_resent: 'Approval e-mail re-sent',
        approval_expired: 'Approval link expired',
        // Print-file and ship-to edits (abo-print-files-upload). Fallbacks for
        // an event without its `kind`; `historyEventLabel` names the file.
        print_file_replaced: 'Print file replaced',
        print_file_restored: 'Print file restored to generated',
        ship_to_changed: 'Ship-to changed',
        // abo-print-files-flipbook-sync; the actor line names who.
        flipbook_replaced: 'Flip book replaced',
        // The Instant Trust Package (itp-mvp). `historyEventLabel` adds the
        // file name of an added file.
        itp_file_added: 'ITP file added',
        itp_file_removed: 'ITP file removed',
        itp_marked_ready: 'ITP marked ready',
      } as Record<string, string>,
      metaLabelUnknown: 'Details updated',
      printFileReplaced: '{kind} replaced',
      printFileRestored: '{kind} restored to generated',
      shipToChangedInvoiceRegenerated: 'Ship-to changed · invoice regenerated',
      itpFileAdded: 'ITP file added · {fileName}',
      itpFileRemoved: 'ITP file removed · {fileName}',
      // Waiting for ITP → Paid, once the ITP is marked ready.
      itpHoldReleased: 'ITP ready · moved to Paid',
    },
    actions: {
      sectionTitle: 'Actions',
      // Compact terminal-state label shown in the hero, e.g. "Shipped — no actions".
      terminal: '{status} — no actions',
      confirmTitle: 'Confirm action',
      confirmMessage: 'Move ticket {orderId} from "{from}" to "{to}"?',
      shipTrackingLabel: 'Shipment reference',
      shipTrackingPlaceholder: 'e.g. 1Z999AA10123456784',
      shipTrackingHint:
        'Leaving this empty asks "Ship without a reference?" before confirming.',
      shipNoTrackingTitle: 'Ship without a reference?',
      shipNoTrackingMessage:
        'No shipment reference was entered for ticket {orderId}. Ship it anyway?',
      shipNoTrackingConfirm: 'Ship without a reference',
      confirmButton: 'Confirm',
      cancelButton: 'Cancel',
      success: 'Ticket {orderId} is now "{status}".',
      error: 'Could not update ticket {orderId}. Please try again.',
      // New in task_06: shown instead of the generic ship-failure message when
      // the ship action 409s because the order already sits in a different
      // open batch. This one string doubles as the clickable link's visible
      // text in the banner (kept to exactly one new key, per task scope).
      orderInOpenBatch:
        'Ticket {orderId} is already in an open batch. View batch →',
    },
    // Member approval of a large book order (book-order-approval, F9/F10/F14).
    // Operator-facing only: none of this copy reaches the member or the
    // printer.
    approval: {
      sectionTitle: 'Member approval',
      // The hero, where the print action would be: there is none yet.
      heroAwaiting: "Waiting for the member's approval",
      heroChangesRequested: 'The member asked for changes',
      // The chip in the card header. An approval names who decided.
      states: {
        awaiting: 'Waiting for the member',
        changes_requested: 'Changes requested',
        approved: 'Approved',
        bypassed: 'Released without approval',
      } satisfies Record<ApprovalState, string>,
      approvedByMemberChip: 'Approved by the member',
      approvedBySupportChip: 'Approved by support',
      // The left column: the state in plain words and what happens next.
      headlines: {
        awaiting: {
          title: 'Waiting for the member',
          body: 'The member has a link to approve the order. Printing starts as soon as they approve. Approve for them only if they confirmed another way.',
        },
        awaiting_expired: {
          title: 'The approval link expired',
          body: "The member's link no longer works. Re-send the approval e-mail to give them a new one, or approve for them if they confirmed another way.",
        },
        changes_requested: {
          title: 'Member asked for changes',
          body: 'Printing is on hold. Contact the member: when they fix the book and click "Update order", they get a new approval e-mail.',
        },
        approved_by_member: {
          title: 'Approved by the member',
          body: 'The member approved the order with their link.',
        },
        approved_by_support: {
          title: 'Approved by support',
          body: "{actor} approved the order on the member's behalf.",
        },
        approved: {
          title: 'Approved',
          body: 'The order was approved by {actor}.',
        },
        bypassed: {
          title: 'Released without approval',
          body: 'Approval was switched off when this order arrived, so it went straight to Paid.',
        },
        // Cancelled before the member decided. The body is also the next step
        // of any cancelled order, so nothing on the card reads as still to do.
        closed: {
          title: 'Approval closed',
          body: 'The order was cancelled, so this approval is closed. Nothing more is sent to the member or the printer.',
        },
      } satisfies Record<ApprovalHeadline, { title: string; body: string }>,
      // An order with an Instant Trust Package (itp-mvp) never goes straight
      // to Paid: it waits for its ITP first.
      bypassedItpBody:
        'Approval was switched off when this order arrived, so it did not wait for the member.',
      // Appended to the headline once the order is released.
      printerNext: {
        pending:
          'The printer has not been e-mailed yet. The daily check retries it.',
        sent: 'The printer has been e-mailed, so it is ready to print.',
        failed:
          'The e-mail to the printer failed and is not retried automatically.',
        not_applicable:
          'The printer e-mail was not held for approval, so it is not sent from here.',
        // itp-mvp: approved, but the order waits for its Instant Trust Package.
        held: 'The printer e-mail is held until the ITP is ready.',
      } satisfies Record<ApprovalPrinterStatus, string>,
      // Pending within ten minutes of the decision: queued, not stuck.
      printerNextSending:
        'Sending the order to the printer now. This usually takes under a minute.',
      emailFailedHint:
        'The e-mail could not be delivered. Re-send it, or contact the member.',
      // {copies} is replaced by the bold "{count} copies".
      copiesCount: '{count} copies',
      changesOverrideNote:
        "Approving now prints the order as it is, at {copies}, without the member's change.",
      notRevisedNote:
        'Approved at {copies} after the member asked for changes. The order was not revised.',
      // The short label–value list under the actions.
      facts: {
        copies: 'Copies',
        linkValidUntil: 'Link valid until',
        linkExpired: 'Expired on {date}',
        approvalLink: 'Approval link',
        linkAnswered: 'Answered by the member',
        reminder: 'Reminder',
        reminderNotSent: 'Not sent yet',
        reminderHint:
          'Goes out once, 72 hours after the request, if the member has not replied.',
        approvedAt: 'Approved',
        approvedBy: 'By',
        releasedAt: 'Released',
        printer: 'Printer e-mail',
        printerSentAt: 'Sent {at}',
        printerSending: 'Sending…',
      },
      actors: {
        member: 'The member',
        admin: 'Support ({id})',
        system: 'Automatically ({id})',
      } satisfies Record<ApprovalActorType, string>,
      printerStatuses: {
        pending: 'Not sent yet',
        sent: 'Sent',
        failed: 'Failed',
        not_applicable: 'Not applicable',
        held: 'Held until the ITP is ready',
      } satisfies Record<ApprovalPrinterStatus, string>,
      // The right column: the approval story, oldest first.
      conversation: {
        title: 'Conversation',
        order: 'Oldest first',
        // Round 1's delivery is known only while the request is the latest
        // e-mail; otherwise the line says what is certain.
        requested: 'Approval requested from the member',
        requestSent: 'Approval request e-mailed to the member',
        requestSending: 'Sending the approval request to the member',
        requestFailed: 'The approval request could not be delivered',
        newRequestSent: 'New approval request e-mailed to the member',
        newRequestSending: 'Sending the new approval request to the member',
        newRequestFailed: 'The new approval request could not be delivered',
        reminderSent: 'Reminder e-mailed to the member',
        reminderSending: 'Sending the reminder to the member',
        reminderFailed: 'The reminder could not be delivered',
        resent: '{actor} re-sent the approval e-mail',
        revised: 'The member updated the order',
        expired: 'The approval link expired',
        // The API keeps only the latest comment; earlier requests show as a line.
        earlierChanges:
          'The member asked for changes. Only the latest comment is shown.',
        memberFallback: 'The member',
        memberCommentKind: 'Requested changes',
        supportReasonKind: "Approved on the member's behalf",
        supportReasonNote: 'Internal reason. Not e-mailed to the member.',
        supportApproved: "{actor} approved the order on the member's behalf",
        memberApproved: 'The member approved the order',
        otherApproved: 'Approved by {actor}',
        bypassed: 'Approval was switched off, so the order went straight to Paid',
        bypassedItp:
          'Approval was switched off, so the order did not wait for the member',
        printerSent: 'New Order e-mail sent to the printer',
        printerFailed: 'The New Order e-mail to the printer failed',
        printerPending: 'The printer has not been e-mailed yet',
        printerSending: 'Sending the New Order e-mail to the printer…',
        printerHeld: 'Printer e-mail held until the ITP is ready',
        noReply: 'No reply from the member yet',
        noDecision: 'No decision yet',
      },
      // Approve on the member's behalf (F9).
      approveButton: "Approve on member's behalf…",
      approveTitle: "Approve on member's behalf",
      approveIntro:
        'Use this when the member approved another way, for example by replying to the e-mail. The order moves to Paid and the printer is notified.',
      // The order's Instant Trust Package is not ready yet (itp-mvp).
      approveIntroItp:
        'Use this when the member approved another way, for example by replying to the e-mail. The order then waits for its Instant Trust Package; the printer is e-mailed once the ITP is ready.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'e.g. Member replied "I approve" on Oct 3',
      reasonHint: 'Between {min} and {max} characters. Recorded on the ticket.',
      reasonTooShort: 'Enter at least {min} characters.',
      reasonTooLong: 'Keep it to {max} characters or fewer.',
      approveConfirm: 'Approve order',
      approveSuccess: 'Order {orderId} approved. It is now in Paid.',
      // DS answered the order in Waiting for ITP (itp-mvp).
      approveSuccessItp:
        'Order {orderId} approved. It now waits for its Instant Trust Package.',
      approveError: 'Could not approve order {orderId}. Please try again.',
      // Re-send the approval e-mail (F10).
      resendButton: 'Re-send approval e-mail',
      resendTitle: 'Re-send the approval e-mail?',
      resendMessage:
        'The member gets a new e-mail with a new link, valid for 14 days. The previous link stops working.',
      resendFromChangesMessage:
        'The order goes back to Awaiting approval.',
      resendConfirm: 'Re-send',
      resendSuccess: 'Approval e-mail re-sent. The new link expires on {date}.',
      resendError:
        'Could not re-send the approval e-mail for order {orderId}. Please try again.',
      cancelButton: 'Cancel',
      // Both actions: someone (the member or another operator) decided first.
      noLongerAwaiting: 'This order is no longer waiting for approval',
    },
    // The Instant Trust Package card of a book order (itp-mvp, option A).
    // Operator-facing only.
    itp: {
      sectionTitle: 'Instant Trust Package',
      // {sets} is `setsCount`.
      included:
        'Included with this plan: {sets}. It ships in the same box as the books.',
      setsCount: (n: number) => (n === 1 ? '1 set' : `${n} sets`),
      states: {
        pending: 'Not ready',
        ready: 'Ready',
      } satisfies Record<TicketItpStatus, string>,
      // The hero, where the print action would be.
      heroWaiting: "Waiting for ITP — can't be accepted for print yet",
      filesTitle: 'ITP files',
      noFiles: 'No ITP files yet.',
      fileMeta: 'Added by {email} · {date} · {size}',
      download: 'Download',
      downloading: 'Preparing…',
      downloadFailed: 'Could not produce a download link. Try again.',
      remove: 'Remove',
      removing: 'Removing…',
      addFiles: 'Add PDF files',
      addFilesHint: 'PDF only, up to 100 MB each.',
      // {name} is the file name; {current} of {total} when several are sent.
      uploading: 'Uploading {name} ({current} of {total})… {percent}%',
      saving: 'Saving {name} ({current} of {total})…',
      // A picked file refused in the browser, before anything is sent.
      fileRefused: '{name}: {reason}',
      fileAdded: '{name} added.',
      fileRemoved: '{name} removed.',
      // Shown after the file name, as `fileRefused`.
      uploadFailed: 'The upload failed. Try again.',
      // The upload loop stopped before these picked files were sent.
      notSent: 'Not sent: {names}. Add them again.',
      removeFailed: 'Could not remove {name}. Try again.',
      markReady: 'Mark ITP ready',
      markReadyHint:
        'This moves the order to Paid, so it can be accepted for print. The printer gets the order with these files.',
      // The order is also waiting for the member's approval.
      markReadyHintApproval:
        "The order still waits for the member's approval. It moves to Paid once the member approves.",
      markReadyNeedsFile: 'Add at least one PDF first.',
      confirmTitle: 'Mark the ITP ready?',
      confirmMessage:
        'Order {orderNumber} moves to Paid and can then be accepted for print.',
      confirmMessageApproval:
        "Order {orderNumber} still waits for the member's approval. It moves to Paid once the member approves.",
      confirmFiles: 'Files',
      confirmFilesCount: (n: number) => (n === 1 ? '1 PDF' : `${n} PDFs`),
      confirmCancel: 'Cancel',
      confirmButton: 'Mark ready',
      markedReady: 'ITP marked ready for order {orderNumber}.',
      markReadyFailed: 'Could not mark the ITP ready. Try again.',
      readyBy: 'Marked ready by {email} · {date}',
      // Marked ready, but the move to Paid did not finish: DS is safe to call
      // again and finishes it.
      finishButton: 'Finish — move to Paid',
      finishHint:
        'The ITP is marked ready, but the order did not move to Paid. Finish the move.',
      // The printer release once the ITP is ready (`itp.printer`).
      printer: {
        label: 'Printer e-mail',
        held: 'Held',
        sending: 'Sending…',
        notSent: 'Not sent yet',
        sent: 'Sent',
        sentAt: 'Sent {at}',
        failed: 'Failed — the printer was not e-mailed; contact engineering',
      },
      // ABO's own text per DS refusal code (`itp-refusal.ts`).
      refusals: {
        ...SHARED_FILE_REFUSALS,
        ITP_NOT_REQUIRED: 'This order does not include an Instant Trust Package.',
        ITP_NOT_EDITABLE:
          'The Instant Trust Package of this order can no longer be changed (already ready, or the order moved on).',
        ITP_NO_BUCKET: NO_BUCKET_REFUSAL,
        ITP_NO_FILES: 'Add at least one ITP PDF before marking the ITP ready.',
        ITP_FILE_NOT_FOUND: 'This ITP file is no longer on the order.',
      } satisfies Record<ItpRefusalCode, string>,
    },
    // Operator cancel of a live book order, and the later "Return credits"
    // (book-order operator cancel). Shown only to holders of the FusionAuth
    // role `print_shop_cancel`; returning credits also needs `credits_adjust`.
    // Cancelling is rare, so the dialog is one note and at most one checkbox.
    cancel: {
      button: 'Cancel order',
      dialogTitle: 'Cancel order #{orderNumber}?',
      loadingPlan: 'Checking the order…',
      blocked: {
        BOOK_CANCEL_NOT_BOOK_ORDER: 'Only book orders can be cancelled here.',
        BOOK_CANCEL_IMPORTED:
          'This is an imported order. It stays in HubSpot and cannot be cancelled here.',
        BOOK_CANCEL_STATUS:
          'This order cannot be cancelled in its current status.',
        BOOK_CANCEL_NOT_SETTLEABLE:
          'There are no credits to return for this order right now.',
      } satisfies Record<BookCancelBlockedReason, string>,
      // A refusal this build has no text for (e.g. one an older DS still sends).
      blockedFallback: 'This order cannot be cancelled here.',
      // The optional reason category that older cancel records carry. ABO no
      // longer asks for one; these only label such records in the history.
      reasonCategories: {
        test_order: 'Test order',
        member_request: 'Member asked to cancel',
        duplicate: 'Duplicate order',
        wrong_files: 'Wrong files',
        fraud: 'Fraud',
        other: 'Other',
      } satisfies Record<BookCancelReasonCategory, string>,
      noteLabel: 'Why?',
      notePlaceholder: 'e.g. Member asked by phone on Oct 18 to cancel',
      noteTooShort: 'Enter at least {min} characters.',
      noteTooLong: 'Keep it to {max} characters or fewer.',
      returnCredits: 'Return the credits to the member',
      noReturnCredits:
        "Credits can't be returned from here. Ask engineering if they should be.",
      // Information only, when the printer may already have the order.
      printerNotice: 'The printer may already have this order.',
      flags: {
        double_charge_suspected:
          'This member may have been charged twice for this order. Report it to engineering.',
      } satisfies Record<BookRefundFlag, string>,
      keepOrder: 'Keep order',
      confirm: 'Cancel order',
      success: 'Order {orderNumber} cancelled.',
      error: 'Could not cancel order {orderNumber}. Your input is kept; try again.',
      conflict: 'This order changed. It was not cancelled; review it again.',
      notFound: 'This order can no longer be cancelled here.',
      unverified: 'Your sign-in could not be verified. Sign in again, then retry.',
      forbidden:
        'You no longer have permission to cancel book orders. Sign in again if this changed recently.',
      unavailable:
        'Cancelling book orders is unavailable right now. Try again later.',
      previewError: 'Could not check this order. Try again.',
      signInAgain: 'Sign in again',
      // The archived view of an operator-cancelled ticket: one line, plus a
      // second only when something needs engineering.
      banner: {
        title: 'Cancelled by support',
        // {reason} is operator-typed text, rendered as text.
        line: '"{reason}" · {credits} · {by}, {date}',
        credits: {
          none: 'credits not returned',
          deferred: 'credits to return after the credits switch',
          ledgerPending: 'credits refund did not complete',
          refunded: '{amount} credits returned',
          settled: '{amount} credits returned on {date}',
          nothingOwed: 'no credits owed',
        },
        ledgerPending:
          'Report this order to engineering; do not return credits another way.',
      },
      // "Return credits" (settle) on an operator-cancelled ticket.
      settle: {
        button: 'Return credits',
        dialogTitle: 'Return {amount} credits?',
        intro: "They go back to the member's balance once.",
        confirm: 'Return credits',
        success: 'Credits returned for order {orderNumber}.',
        error:
          'Could not return the credits for order {orderNumber}. Try again.',
      },
    },
    // Status CORRECTION (task 60): a deliberate override to fix a wrongly-set
    // status, distinct from the forward action. The member is not notified and
    // credits are unchanged — copy makes that clear so it reads as a fix.
    correctStatus: {
      editButton: 'Edit status',
      editAriaLabel: "Correct this ticket's status",
      dialogTitle: 'Correct ticket status',
      intro:
        "Fix a wrongly-set status. The member isn't notified and credits aren't changed.",
      statusLabel: 'Set status to',
      statusPlaceholder: 'Choose a status…',
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'e.g. marked shipped by mistake',
      confirmButton: 'Save correction',
      cancelButton: 'Cancel',
      success: 'Ticket {orderId} status corrected to "{status}".',
      error: 'Could not correct ticket {orderId}. Please try again.',
      // 409 `TICKET_STATUS_HELD` (itp-mvp).
      held: 'Ticket {orderId} is waiting for its Instant Trust Package. Mark the ITP ready instead.',
    },
  },
  statusLabels: {
    // The approval lanes before Paid (book-order-approval, F14).
    awaiting_approval: 'Awaiting approval',
    changes_requested: 'Changes requested',
    // The ITP lane before Paid (itp-mvp).
    itp_pending: 'Waiting for ITP',
    paid: 'Paid',
    accepted: 'Accepted',
    in_production: 'In production',
    shipped: 'Shipped',
    cancelled: 'Cancelled',
    imported: 'Imported',
  } as Record<string, string>,
  actionLabels: {
    accept: 'Accept',
    start_production: 'Start production',
    ship: 'Ship',
  } as Record<TicketAction, string>,
  assetKindLabels: {
    book: 'Book',
    tb_export: 'Template Builder export',
    cover_letter: 'Cover Letter',
    book_cover_letter_bundle: 'Book + Cover Letter Bundle',
  } as Record<AssetKind, string>,
} as const;

/**
 * Replaces `{token}` placeholders in a message with the provided values.
 * Unknown tokens are left intact so missing interpolation is visible rather
 * than silently dropped.
 */
export const formatMessage = (
  template: string,
  vars: Record<string, string> = {},
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match,
  );
